import { useEffect, useRef, useState } from 'react'

const W = 320
const POINTS = 80
const SPEED = 1.2
const FREQ_SCALE = 1.05

// The pure tones the composite waveform is built from — low frequency/high
// amplitude first, rising in frequency and dropping in amplitude, same as
// the desktop scene's stack.
const COMPONENTS = [
  { freq: 1.0, amp: 1.0, color: '#ff5a6e' },
  { freq: 2.0, amp: 0.6, color: '#ffb14a' },
  { freq: 3.3, amp: 0.42, color: '#39e0c8' },
  { freq: 4.7, amp: 0.3, color: '#5aa0ff' },
  { freq: 6.2, amp: 0.22, color: '#b07bff' },
]

const composite = (u, t) =>
  COMPONENTS.reduce((sum, c) => sum + c.amp * Math.sin(u * c.freq * FREQ_SCALE - t), 0) * 0.55

// Samples fn(u, t) for u in [-3, 3] across the full width into an SVG path.
function wavePath(yOffset, ampPx, fn, t) {
  let d = ''
  for (let i = 0; i <= POINTS; i++) {
    const p = i / POINTS
    const u = (p - 0.5) * 6
    const x = p * W
    const y = yOffset - fn(u, t) * ampPx
    d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
  }
  return d
}

// Vertical layout for narrow screens: one composite waveform enters a prism,
// splits into its five pure-tone bands, then recombines back into the same
// waveform — mirroring the desktop scene's "add the tones back" idea.
export function Scene03Fourier2D() {
  const [t, setT] = useState(0)
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      setT(((now - start) / 1000) * SPEED)
      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const bandY0 = 260
  const bandGap = 34

  return (
    <svg viewBox="0 0 320 600" style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      {/* Original composite waveform */}
      <text x={W / 2} y="16" fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.1em">ORIGINAL WAVE</text>
      <path d={wavePath(50, 22, composite, t)} fill="none" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

      <text x={W / 2} y="92" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      {/* Prism */}
      <polygon points={`${W / 2},108 ${W / 2 + 46},178 ${W / 2 - 46},178`} fill="#bcd8ff" opacity="0.1" stroke="#cfe6ff" strokeWidth="1.5" />

      <text x={W / 2} y="204" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      {/* Five pure-tone bands, low frequency/high amplitude at top */}
      <text x={W / 2} y="230" fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.1em">SPLIT INTO PURE TONES</text>
      {COMPONENTS.map((c, i) => {
        const y = bandY0 + i * bandGap
        return (
          <path
            key={c.freq}
            d={wavePath(y, 14 * c.amp, (u, tt) => Math.sin(u * c.freq * FREQ_SCALE - tt), t)}
            fill="none" stroke={c.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.92"
          />
        )
      })}

      <text x={W / 2} y={bandY0 + COMPONENTS.length * bandGap + 26} fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      {/* Recombined: sum of the tones = the original wave again */}
      <text x={W / 2} y={bandY0 + COMPONENTS.length * bandGap + 52} fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.1em">Σ = ORIGINAL WAVE</text>
      <path
        d={wavePath(bandY0 + COMPONENTS.length * bandGap + 90, 22, composite, t)}
        fill="none" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  )
}
